import { FC, ReactNode, RefObject, useMemo, useRef } from "react";

import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import Image from "common/blocks/Image";
import SmartLink from "common/wrapper/SmartLink";
import uniqueId from "lodash/uniqueId";
import { ResolvedMegaMenuPromo } from "resolvers/base/megaMenuSectionPromoResolver";
import {
    MegaMenuSectionEnum,
    ResolvedMegaMenuSection,
} from "resolvers/base/megaMenuSectionResolver";
import { ResolvedStockNumberSearch } from "resolvers/base/stockNumberSearchResolver";
import {
    ResolvedMegaMenuCtaButton,
    ResolvedMegaMenuItem,
} from "resolvers/data/header/megaMenuResolver";
import { ContentTypeCodenames } from "services/cms/models/contentTypeCodenames";
import "swiper/css";
import { A11y, Navigation } from "swiper/modules";
import { Swiper } from "swiper/react";
import { Swiper as ISwiper } from "swiper/types";

import { MenuHeader } from "../MenuHeader";
import MobileStockNumberSearch from "../MobileStockNumberSearch";
import { StepView } from "../interfaces";
import styles from "./MegaMenu.module.scss";

const cx = classNames.bind(styles);

type SwiperEl = ReactNode & { swiper: ISwiper };

export type MegaMenuMobileProps = {
    megaMenuItems: ResolvedMegaMenuItem[];
    handleBackStepView: () => void;
    stepViewData: StepView;
    stockNumberSearch: ResolvedStockNumberSearch;
    prefetch?: boolean;
    menuRef: RefObject<HTMLDivElement>;
};

export const MegaMenu: FC<MegaMenuMobileProps> = ({
    megaMenuItems,
    handleBackStepView,
    stepViewData,
    stockNumberSearch,
    prefetch = false,
    menuRef,
}) => {
    const swiperRef = useRef<null | SwiperEl>(null);

    const swiperOpts = {
        // 1.01 - to display the border to the right
        slidesPerView: 1.01,
        slidesPerGroupAuto: true,
        className: cx("swiper"),
        modules: [A11y, Navigation],
        ref: swiperRef,
        simulateTouch: false,
        spaceBetween: 10,
    };

    const { stepData } = stepViewData;
    const menuHeaderTitle = stepData ? stepData.text : "";
    const megaMenu = stepData ? megaMenuItems.find((e) => e.id === stepData.megaMenuId) : null;

    const { sections } = megaMenu ?? {};

    const { menuItems, menuPromos } = useMemo(() => {
        return sections?.reduce(
            (result: any, section: ResolvedMegaMenuSection) => {
                switch (section.type) {
                    case MegaMenuSectionEnum.Links: {
                        result.menuItems.push({
                            title: section.title,
                            links: section?.body?.[0]?.body?.map((link: any) => ({
                                link: link.link,
                                text: link.text,
                            })),
                            ctaButtonsPosition: section?.ctaButtons?.position,
                            ctaButtons: section?.ctaButtons?.body?.map((ctaButton: any) => ({
                                link: ctaButton.link,
                                text: ctaButton.text,
                                priority: ctaButton.priority,
                            })),
                        });
                        break;
                    }
                    case MegaMenuSectionEnum.Banners: {
                        result.menuPromos.push(
                            ...(section?.body?.[0]?.body as ResolvedMegaMenuPromo[])
                        );
                    }
                }
                return result;
            },
            {
                menuItems: [],
                menuPromos: [],
            }
        );
    }, [sections]) ?? {
        menuItems: [],
        menuPromos: [],
    };

    const isStockNumberSearch = stepData?.id === ContentTypeCodenames.StockNumberSearch;

    const renderButton = (ctaButton: ResolvedMegaMenuCtaButton) => (
        <span key={ctaButton.text} className={cx("browseButtonWrapper")}>
            <Button
                className={cx("browseButton")}
                href={ctaButton.link}
                theme={ctaButton.priority === "secondary" ? "primary" : "accent"}
                target={ctaButton.target}
            >
                {ctaButton.text}
            </Button>
        </span>
    );

    // @TODO key value must be set as a string after integration with CMS
    return (
        <div style={{ height: "100%" }}>
            <div className={cx("wrapper")}>
                <MenuHeader handleBackStepView={handleBackStepView} title={menuHeaderTitle} />
                <div className={cx("content")}>
                    {menuItems && !isStockNumberSearch && (
                        <>
                            <ul className={cx("list")}>
                                {menuItems?.map((subMenuItem: any) => (
                                    <li className={cx("listSection")} key={uniqueId()}>
                                        {subMenuItem?.ctaButtons?.map((ctaButton: any) =>
                                            subMenuItem.ctaButtonsPosition === "top"
                                                ? renderButton(ctaButton)
                                                : null
                                        )}
                                        {subMenuItem?.title?.text && (
                                            <span className={cx("listSectionTitle")}>
                                                {subMenuItem.title.text}
                                            </span>
                                        )}
                                        {subMenuItem?.links && (
                                            <ul className={cx("listSubList")}>
                                                {subMenuItem.links.map((item: any) => (
                                                    <li
                                                        className={cx("listSubListItem")}
                                                        key={item.text}
                                                    >
                                                        <SmartLink
                                                            href={item.link}
                                                            prefetch={!!prefetch}
                                                        >
                                                            <a
                                                                className={cx(
                                                                    "listSubListItemLink"
                                                                )}
                                                            >
                                                                {item.text}
                                                            </a>
                                                        </SmartLink>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        {subMenuItem?.ctaButtons?.map((ctaButton: any) =>
                                            subMenuItem.ctaButtonsPosition === "bottom"
                                                ? renderButton(ctaButton)
                                                : null
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {isStockNumberSearch && (
                        <MobileStockNumberSearch stockNumberSearch={stockNumberSearch} />
                    )}
                    {!!menuPromos?.length && (
                        <div className={cx("swipeContainer")}>
                            <Swiper {...swiperOpts}>
                                {menuPromos.map((item: any) => (
                                    <div className={cx("swipeItemWrapper")} key={item.id}>
                                        <div className={cx("swipeItemImageWrapper")}>
                                            <Image
                                                className={cx("swipeItemImage")}
                                                src={item.image.src}
                                                layout="fill"
                                                alt={item.image.alt || item.label}
                                            />
                                        </div>
                                        <div className={cx("swipeItemContent")}>
                                            <span className={cx("swipeItemContentTitle")}>
                                                {item.label}
                                            </span>
                                            <span className={cx("swipeItemContentDescription")}>
                                                {item.description}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </Swiper>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
